import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { MessageFeed, MessagePopup } from "@/components/MessageBoard";
import ReadyToPublishInbox from "@/components/ReadyToPublishInbox";
import EbayCrossPostInbox from "@/components/EbayCrossPostInbox";
import { NiftySyncWidget } from "@/components/NiftySyncWidget";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { formatMoney } from "@/lib/currency";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/inventory").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const totalRevenue = items.reduce((a, i) => a + (i.sale_price || 0), 0);
  const inStudio = items.filter((i) => ["Photographing", "Studio Editing"].includes(i.stage)).length;
  const listed = items.filter((i) => i.stage === "Listing & Marketing").length;
  const sold = items.filter((i) => ["Sale Completion", "Packaging", "Shipping", "Confirmation of Receipt"].includes(i.stage)).length;

  const stats = [
    { label: "pieces in atelier", value: items.length, testid: "stat-total" },
    { label: "in studio", value: inStudio, testid: "stat-studio" },
    { label: "currently listed", value: listed, testid: "stat-listed" },
    { label: "in fulfilment", value: sold, testid: "stat-sold" },
  ];

  return (
    <AdminLayout>
      <MessagePopup user={user} />
      <div data-testid="admin-dashboard">
        <span className="editorial-label">overview · today</span>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-2">the atelier, at a glance.</h1>
        <p className="text-espresso max-w-2xl">beautiful things deserve another story. here is yours, today.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {stats.map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-linen border border-taupe/50 p-8">
              <p className="editorial-label">{s.label}</p>
              <p className="font-serif text-5xl text-charcoal mt-3">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 bg-linen border border-taupe/50 p-8">
          <p className="editorial-label">projected revenue</p>
          <p className="font-serif text-5xl text-brass mt-3">{formatMoney(totalRevenue)}</p>
          <p className="text-xs text-espresso mt-3">sum of computed sale prices across the active inventory.</p>
        </div>

        <div className="mt-6">
          <NiftySyncWidget />
        </div>

        <div className="mt-14">
          <ReadyToPublishInbox />
        </div>

        <div className="mt-14">
          <EbayCrossPostInbox />
        </div>

        <div className="mt-14">
          <MessageFeed user={user} />
        </div>

        <div className="mt-14 flex items-end justify-between">
          <h2 className="font-serif text-3xl lowercase text-charcoal">recently added</h2>
          <Link to="/admin/inventory" className="editorial-label hover:text-brass">view all →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {items.slice(0, 6).map((it, i) => (
            <Link key={it.id} to={`/admin/inventory/${it.id}`} className="border border-taupe/40 bg-ivory p-5 hover:border-brass transition-colors" data-testid={`recent-item-${i}`}>
              <div className="aspect-[4/3] bg-linen overflow-hidden mb-4">
                {it.photos?.[0] ? (
                  <img src={fileUrl(it.photos[0])} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-taupe font-serif text-sm">no photo yet</div>
                )}
              </div>
              <p className="editorial-label">{it.stage.toLowerCase()}</p>
              <h3 className="font-serif text-xl lowercase mt-1 text-charcoal">{it.title}</h3>
              <p className="text-sm text-espresso mt-1">{formatMoney(it.sale_price)}</p>
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
